import { makeAutoObservable } from 'mobx';
import moment from 'moment/moment';

import { GridStore } from 'controls/grid/gridStore';
import { DatasourceCategorySelectorProps } from './types';
import { RemoteDataProvider } from 'controls/grid/remoteDataProvider';
import { i18n } from 'controls/designer/features/widgets/datasources/tile/localization';
import { TimePeriodType } from 'controls/react/form/timePeriodType';
import { GridSelectionType } from 'controls/grid/gridConfiguration';
import { GridColumnConfig, GridColumnRendererExtra } from 'controls/grid/gridColumnConfig';
import { FieldType } from 'controls/queryBuilder/ruleDefinition';
import { ApplicationState } from 'framework/applicationState';
import { MobxManager } from 'framework/mobx-integration';
import { updatedPayloadToLoadAllData } from 'controls/designer/features/widgets/datasources/shared/utils/updatedPayloadToLoadAllData';

export class DataSourceCategorySelectorStore {
	gridStore: GridStore<{ [id: string]: string | number }>;
	rowsWarningMessage: string;
	columnsWarningMessage: string;

	mobx = new MobxManager();
	props: DatasourceCategorySelectorProps;

	constructor() {
		makeAutoObservable(this);
	}

	get manager() {
		return this.props?.manager;
	}

	get selection() {
		return this.manager.config.selection;
	}

	init = () => {
		this.mobx.reaction(
			() => this.manager?.createGetDataSourceDataRequest(),
			(dataRequest) => {
				this.gridStore?.destroy();

				if (!dataRequest) return;

				updatedPayloadToLoadAllData(dataRequest);

				this.gridStore = new GridStore<{ [id: string]: string | number }>({
					columns: this.getColumns(),
					dataProvider: new RemoteDataProvider({
						dataSource: dataRequest,
						filtersConfiguration: this.manager.rulesConfiguration.data,
						onBatchLoaded: (response) => {
							if (response.data.warning) {
								this.columnsWarningMessage = response.data.warning;
							}
							if (response.data.total > response.data.maxRows) {
								this.rowsWarningMessage = i18n('ROWS_WARNING_MESSAGE', response.data.maxRows, response.data.total);
							}
						},
					}),
					defaults: {
						payload: {
							timePeriod: {
								period: TimePeriodType.Last24Hours,
							},
							showHistoricEvents: false,
						},
					},
					selection: GridSelectionType.None,
				});
			},
			{
				fireImmediately: true,
			},
		);
	};

	getColumns = () => {
		const rules = this.manager.rulesConfiguration.data;
		return Object.keys(rules)
			.filter((x) => x != 'id')
			.map((columnName) => {
				const column: GridColumnConfig<{ [id: string]: string | number }> = {
					field: columnName,
					title: rules[columnName].label,
					renderer: (
						item: { [id: string]: string | number },
						extra: GridColumnRendererExtra<{ [id: string]: string | number }>,
					) => {
						const selectedRow =
							this.selection.rowKey &&
							this.selection.rowKeyValue &&
							this.selection.rowKeyValue == extra.gridItem.data[this.selection.rowKey];

						const selectedCell = selectedRow && this.selection.rowKey == columnName;

						const styles = selectedRow
							? {
									backgroundColor: '#d7eefd',
									...(selectedCell ? { border: '1px solid #1677ff' } : null),
								}
							: null;

						const onClick = () => {
							this.selection.rowKey = columnName;
							this.selection.rowKeyValue = item[columnName] as string;
						};

						if (rules[columnName].type == FieldType.Number) {
							try {
								return { content: (item[columnName] as number)?.toFixed(0) ?? '', styles, onClick };
							} catch (e) {
								console.error(`value: ${item[columnName]}`, e);
								return { content: item[columnName], styles, onClick };
							}
						}
						if (rules[columnName].type == FieldType.Date && !!item[columnName]) {
							return {
								content: moment(item[columnName]).format(ApplicationState.momentDateTimeFormat),
								styles,
								onClick,
							};
						}
						return { content: item[columnName] ?? '', styles, onClick };
					},
				};
				return column;
			});
	};

	closeRowsWarning = () => {
		this.rowsWarningMessage = undefined;
	};

	closeColumnsWarning = () => {
		this.columnsWarningMessage = undefined;
	};

	destroy = () => {
		this.gridStore?.destroy();
		this.mobx.destroy();
	};
}
